import { type ComponentProps, type FC, useMemo } from 'react';
import { CartItemV2 as BaseCartItemV2 } from '@kidslabs/mario-nextjs-react-ui-core';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'next-i18next';
import { cartStoreAPI } from '../../stores/cart-store.public-api';
import { CartItemTypeEnum } from '../../models/cart.model';

type BaseCartItemV2Props = ComponentProps<typeof BaseCartItemV2> & {
    stockAvailability?: { isSalable: boolean; loading: boolean };
};

/**
 * CART ITEM V2 WRAPPER - MODULE QUOTE
 *
 * This wrapper adds business logic before rendering ui-core CartItemV2:
 *
 * 1. STOCK AVAILABILITY INJECTION:
 *    - Consumes stock data from StockAvailabilityContext
 *    - Extracts SKU from cart item
 *    - Passes stock data to base component
 *
 * 2. VISUAL STATE MANAGEMENT:
 *    - Auto-disable items that are out of stock
 *    - Show appropriate out-of-stock indicators
 *
 * ARCHITECTURE:
 * - This component wraps ui-core/CartItemV2
 * - Consumes StockAvailabilityContext from parent CartItemGroup
 * - Maintains separation of concerns (UI vs Business Logic)
 *
 * USAGE:
 * ```typescript
 * import {CartItem} from '@kidslabs/mobile-ecommerce-module-quote';
 *
 * // Must be used inside CartItemGroup to access stock context
 * <CartItemGroup>
 *   <CartItem item={item} />
 * </CartItemGroup>
 * ```
 *
 * @see mobile-ecommerce/applications/module-quote/src/components/cart-item/cart-item.tsx
 */
export const CartItem: FC<BaseCartItemV2Props> = props => {
    const { item, disabled } = props;
    const { t } = useTranslation('quote');

    // GET STOCK DATA FROM Legend-State store (matches mobile pattern)
    const stockAvailabilityMap = useSelector(() => cartStoreAPI.state.getAvailableItemsObservable().get());

    // Extract SKU for stock lookup
    const sku = useMemo(() => {
        if (!item) return undefined;
        const isConfigurableItem =
            item?.__typename === CartItemTypeEnum.CONFIGURABLE ||
            item?.__typename === CartItemTypeEnum.CONFIGURABLE_LOCAL;

        return isConfigurableItem ? item?.configured_variant?.sku : item?.product?.sku;
    }, [item]);

    // ✅ GET STOCK DATA: From Legend-State store (reactive, matches mobile)
    const stockAvailability = useMemo(() => {
        if (!sku) return { isSalable: true, loading: false, availableQuantity: null };
        const entry = stockAvailabilityMap?.[sku];

        return {
            isSalable: entry?.isSalable ?? false,
            loading: false,
            availableQuantity: entry?.availableQuantity ?? null,
        };
    }, [sku, stockAvailabilityMap]);
    const { availableQuantity, isSalable } = stockAvailability;

    // ✅ AUTO-DISABLE: If item is out of stock, mark as disabled (matches mobile logic)
    const effectiveDisabled = useMemo<boolean | undefined>(() => {
        // If already disabled, keep it
        if (disabled) return disabled;

        // If stock is not salable and not loading, disable the item
        if (!stockAvailability.isSalable && !stockAvailability.loading) {
            return true; // Disable content but allow viewing
        }

        return undefined;
    }, [disabled, stockAvailability]);

    // Generate stock message with enhanced information (matches mobile)
    const outOfStockMessage = useMemo(() => {
        if (!isSalable && availableQuantity !== null && availableQuantity <= 0) {
            return t('text_out_of_stock_badge');
        }
        return undefined;
    }, [isSalable, availableQuantity, t]);

    // Pass enhanced props to base component
    const enhancedProps: BaseCartItemV2Props = {
        ...props,
        disabled: effectiveDisabled,
        outOfStockMessage,
    };

    return <BaseCartItemV2 {...enhancedProps} />;
};

// Re-export base CartItemV2 for cases where stock context is not needed
export { CartItemV2 as BaseCartItem } from '@kidslabs/mario-nextjs-react-ui-core';
